/**
 * FRIDGE-1260 — the most one household can actually collect from a utility.
 *
 * The rebate pages used to add every active row together. Rows in the same
 * category for the same appliance are ALTERNATIVE tiers, not add-ons: PPL
 * Electric pays $75 for an ENERGY STAR Most Efficient refrigerator OR $50 for
 * an ENERGY STAR one, plus $35 to recycle the old unit — $110 at most — and
 * the page said "Up to $160". A rebate total may understate; it must never
 * overstate.
 *
 * The rule, per category (purchase, recycling):
 *   - one refrigerator and one freezer can each earn a rebate, so a
 *     refrigerator row and a freezer row add up;
 *   - several rows for the same appliance are alternatives — only the best
 *     one counts;
 *   - a `both` row covers EITHER appliance once, so it fills one slot, never
 *     two (it is one program, not a per-appliance promise).
 * Categories then add up: buying and recycling are separate benefits.
 */

export interface RebateAmountRow {
  rebate_category: string
  product_type: string
  rebate_amount_cents: number | null
}

function best(rows: RebateAmountRow[], productType: string): number {
  let max = 0
  for (const r of rows) {
    if (r.product_type === productType && (r.rebate_amount_cents ?? 0) > max) max = r.rebate_amount_cents ?? 0
  }
  return max
}

/** The best total one household can collect across all the given rows, in cents. */
export function maxCombinedRebateCents(rows: readonly RebateAmountRow[]): number {
  const byCategory = new Map<string, RebateAmountRow[]>()
  for (const r of rows) {
    if (!r.rebate_amount_cents || r.rebate_amount_cents <= 0) continue
    byCategory.set(r.rebate_category, [...(byCategory.get(r.rebate_category) ?? []), r])
  }
  let total = 0
  for (const categoryRows of byCategory.values()) {
    const fridge = best(categoryRows, 'refrigerator')
    const freezer = best(categoryRows, 'freezer')
    const either = best(categoryRows, 'both')
    // The `both` row fills at most one slot: the fridge's, the freezer's, or neither.
    total += Math.max(fridge + freezer, either + freezer, fridge + either)
  }
  return total
}
